import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import SendIcon from "@mui/icons-material/Send";
import StopIcon from "@mui/icons-material/Stop";
import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import { isMobileWebBrowser, mobile } from "src/utils/platformUtils";

export type ChatBotQuestionInputHandle = {
  getValue: () => string;
  setValue: (value: string) => void;
  clear: () => void;
};

type Props = {
  disabled: boolean;
  hasActiveTurn: boolean;
  isMicEnabled: boolean;
  isRecording: boolean;
  isUserSpeaking: boolean;
  micPermissionDeniedHint: boolean;
  maxLength: number;
  queuedMessages: string[];
  onSend: (text: string) => void;
  onCancelResponse: () => void;
  onRemoveQueuedAt: (index: number) => void;
  onToggleMic: () => void;
  onStopMicFromTyping: () => void;
  onStableTextChange: (text: string) => void;
};

const iconButtonSx = {
  p: 0.5,
  minWidth: 36,
  minHeight: 36,
  color: PureLightThemeCaaS.colors.alpha.white[100],
  "&:hover": { backgroundColor: PureLightThemeCaaS.colors.alpha.white[10] },
  "&.Mui-disabled": {
    color: PureLightThemeCaaS.colors.alpha.white[30],
  },
} as const;

const ChatBotQuestionInput = forwardRef<ChatBotQuestionInputHandle, Props>(
  function ChatBotQuestionInput(
    {
      disabled,
      hasActiveTurn,
      isMicEnabled,
      isRecording,
      isUserSpeaking,
      micPermissionDeniedHint,
      maxLength,
      queuedMessages,
      onSend,
      onCancelResponse,
      onRemoveQueuedAt,
      onToggleMic,
      onStopMicFromTyping,
      onStableTextChange,
    },
    ref,
  ) {
    const [textInput, setTextInput] = useState("");
    const [isQueueExpanded, setIsQueueExpanded] = useState(true);
    const textFieldInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
    const scrollInputToEnd = () => {
      const el = textFieldInputRef.current;
      if (!el) return;
      requestAnimationFrame(() => {
        el.scrollLeft = el.scrollWidth;
        el.scrollTop = el.scrollHeight;
      });
    };

    useImperativeHandle(
      ref,
      () => ({
        getValue: () => textInput,
        setValue: (value: string) => {
          setTextInput(value);
          scrollInputToEnd();
        },
        clear: () => {
          setTextInput("");
        },
      }),
      [textInput],
    );

    const handleSend = () => {
      const trimmed = textInput.trim();
      if (!trimmed) return;
      onSend(trimmed);
      setTextInput("");
      onStableTextChange("");
    };

    const isAtMaxLength = textInput.length >= maxLength;
    const isMobile = isMobileWebBrowser() || mobile;
    const hasText = Boolean(textInput.trim());

    return (
      <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
        {queuedMessages.length > 0 ? (
          <Box
            sx={{
              width: "90%",
              maxWidth: "90%",
              margin: "0 auto",
              mb: 0.75,
              borderRadius: 1,
              border: `1px solid ${PureLightThemeCaaS.colors.alpha.white[10]}`,
              background: "rgba(255,255,255,0.04)",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1,
                py: 0.35,
              }}
            >
              <Typography
                sx={{
                  color: PureLightThemeCaaS.colors.alpha.white[50],
                  fontSize: "10px",
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                }}
              >
                Queued
                <Box
                  component="span"
                  sx={{
                    ml: 0.75,
                    color: PureLightThemeCaaS.colors.alpha.white[30],
                    fontWeight: 600,
                    letterSpacing: 0,
                    textTransform: "none",
                  }}
                >
                  {queuedMessages.length}
                </Box>
              </Typography>
              <IconButton
                size="small"
                aria-label={isQueueExpanded ? "hide-queue" : "show-queue"}
                onClick={() => setIsQueueExpanded((prev) => !prev)}
                sx={{
                  p: 0.2,
                  color: PureLightThemeCaaS.colors.alpha.white[50],
                  "&:hover": {
                    color: PureLightThemeCaaS.colors.alpha.white[100],
                    backgroundColor: PureLightThemeCaaS.colors.alpha.white[10],
                  },
                }}
              >
                {isQueueExpanded ? (
                  <ExpandLessIcon sx={{ fontSize: 18 }} />
                ) : (
                  <ExpandMoreIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </Box>
            {isQueueExpanded ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                // Exactly 3 rows (30px each); scrollbar from the 4th onward
                maxHeight: 90,
                overflowY: "auto",
                px: 0.25,
                pb: 0,
                scrollbarWidth: "thin",
                "&::-webkit-scrollbar": { width: 3 },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: PureLightThemeCaaS.colors.alpha.white[20],
                  borderRadius: 4,
                },
              }}
            >
              {queuedMessages.map((msg, index) => (
                <Box
                  key={`${index}-${msg.slice(0, 24)}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    px: 0.5,
                    height: 30,
                    flexShrink: 0,
                    borderRadius: 0.75,
                    "&:hover": {
                      backgroundColor: PureLightThemeCaaS.colors.alpha.white[5],
                    },
                  }}
                >
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: PureLightThemeCaaS.colors.alpha.white[10],
                      border: `1px solid ${PureLightThemeCaaS.colors.alpha.white[20]}`,
                    }}
                  >
                    <Typography
                      sx={{
                        color: PureLightThemeCaaS.colors.alpha.white[70],
                        fontSize: "9px",
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                    >
                      {index + 1}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      color: PureLightThemeCaaS.colors.alpha.white[100],
                      fontSize: "12px",
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      lineHeight: 1.25,
                    }}
                  >
                    {msg}
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label={`remove-queued-message-${index + 1}`}
                    onClick={() => onRemoveQueuedAt(index)}
                    disabled={disabled}
                    sx={{
                      p: 0.2,
                      color: PureLightThemeCaaS.colors.alpha.white[50],
                      "&:hover": {
                        color: PureLightThemeCaaS.colors.alpha.white[100],
                        backgroundColor: PureLightThemeCaaS.colors.alpha.white[10],
                      },
                      "&.Mui-disabled": { color: PureLightThemeCaaS.colors.alpha.white[30] },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
            ) : null}
          </Box>
        ) : null}

        <Box
          display="flex"
          flexDirection="row"
          justifyContent="center"
          alignItems="center"
          sx={{ width: "100%" }}
        >
          <TextField
            inputRef={textFieldInputRef}
            value={textInput}
            multiline
            minRows={1}
            maxRows={6}
            onChange={(e) => {
              const next = e.target.value;
              onStopMicFromTyping();
              setTextInput(next);
              onStableTextChange(next);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && textInput.trim()) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Enter your question here"
            disabled={disabled}
            inputProps={{ maxLength }}
            variant="outlined"
            size="medium"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ gap: 0.25 }}>
                  {hasActiveTurn ? (
                    <>
                      <IconButton
                        onClick={onCancelResponse}
                        aria-label="stop-response"
                        disabled={disabled}
                        sx={iconButtonSx}
                      >
                        <StopIcon sx={{ fontSize: `${isMobile ? "22" : "24"}px` }} />
                      </IconButton>
                      {hasText ? (
                        <IconButton
                          disabled={disabled}
                          onClick={handleSend}
                          aria-label="queue-or-send"
                          sx={iconButtonSx}
                        >
                          <SendIcon sx={{ fontSize: `${isMobile ? "20" : "22"}px` }} />
                        </IconButton>
                      ) : null}
                    </>
                  ) : hasText && !isMicEnabled ? (
                    <IconButton
                      disabled={disabled}
                      onClick={handleSend}
                      sx={iconButtonSx}
                    >
                      <SendIcon sx={{ fontSize: `${isMobile ? "20" : "22"}px` }} />
                    </IconButton>
                  ) : (
                    <Tooltip
                      title={micPermissionDeniedHint ? undefined : "Use microphone"}
                      placement="top"
                      {...(micPermissionDeniedHint && { open: true })}
                      arrow
                    >
                      <Box
                        component="span"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "50%",
                          minWidth: 36,
                          minHeight: 36,
                          transition: "box-shadow 0.2s ease-out, background-color 0.15s ease",
                          ...(isMicEnabled
                            ? {
                              boxShadow: isRecording
                                ? isUserSpeaking
                                  ? `0 0 10px 6px ${PureLightThemeCaaS.colors.alpha.orange[70]}, 0 0 16px 4px ${PureLightThemeCaaS.colors.alpha.orange[50]}`
                                  : `0 0 6px 4px ${PureLightThemeCaaS.colors.alpha.orange[70]}, 0 0 14px 3px ${PureLightThemeCaaS.colors.alpha.orange[50]}`
                                : `0 0 10px 6px ${PureLightThemeCaaS.colors.alpha.orange[70]}, 0 0 16px 4px ${PureLightThemeCaaS.colors.alpha.orange[50]}`,
                            }
                            : { "&:hover": { backgroundColor: PureLightThemeCaaS.colors.alpha.white[10] } }),
                        }}
                      >
                        <IconButton
                          size="small"
                          disabled={disabled}
                          sx={{
                            p: 0,
                            borderRadius: "50%",
                            minWidth: 36,
                            minHeight: 36,
                            color: PureLightThemeCaaS.colors.alpha.white[100],
                            "&:hover": {
                              backgroundColor: isMicEnabled
                                ? PureLightThemeCaaS.colors.alpha.orange[10]
                                : "transparent",
                            },
                            "&.Mui-disabled": { color: PureLightThemeCaaS.colors.alpha.white[30] },
                          }}
                          aria-label={isMicEnabled ? "mute-microphone" : "unmute-microphone"}
                          onClick={onToggleMic}
                        >
                          {micPermissionDeniedHint ? (
                            <MicOffIcon sx={{ fontSize: `${isMobile ? "22" : "25"}px` }} />
                          ) : (
                            <MicIcon sx={{ fontSize: `${isMobile ? "22" : "25"}px` }} />
                          )}
                        </IconButton>
                      </Box>
                    </Tooltip>
                  )}
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              maxWidth: "90%",
              "& .MuiOutlinedInput-root": {
                color: PureLightThemeCaaS.colors.alpha.white[100],
                backgroundColor: "transparent",
                alignItems: "flex-end",
                "& .MuiInputAdornment-positionEnd": {
                  alignSelf: "flex-end",
                  marginBottom: "11px",
                },
                "& .MuiInputBase-input": {
                  color: PureLightThemeCaaS.colors.alpha.white[100],
                  WebkitTextFillColor: PureLightThemeCaaS.colors.alpha.white[100],
                  scrollbarWidth: "none",
                  "&::-webkit-scrollbar": { display: "none" },
                  "&::placeholder": {
                    color: PureLightThemeCaaS.colors.alpha.white[50],
                    opacity: 1,
                    WebkitTextFillColor: PureLightThemeCaaS.colors.alpha.white[50],
                  },
                },
                "& fieldset": {
                  borderColor: isAtMaxLength ? "#d32f2f" : PureLightThemeCaaS.colors.alpha.white[30],
                },
                "&:hover fieldset": {
                  borderColor: isAtMaxLength ? "#d32f2f" : PureLightThemeCaaS.colors.alpha.white[50],
                },
                "&.Mui-focused fieldset": {
                  borderColor: isAtMaxLength ? "#d32f2f" : PureLightThemeCaaS.colors.alpha.white[100],
                  borderWidth: "1px",
                },
                "&.Mui-disabled": {
                  backgroundColor: "rgba(255, 255, 255, 0.06)",
                  "& .MuiInputBase-input": {
                    WebkitTextFillColor: PureLightThemeCaaS.colors.alpha.white[50],
                    color: PureLightThemeCaaS.colors.alpha.white[50],
                  },
                  "& fieldset": {
                    borderColor: isAtMaxLength ? "#d32f2f" : PureLightThemeCaaS.colors.alpha.white[30],
                  },
                },
                "&.Mui-disabled.Mui-focused fieldset": {
                  borderColor: isAtMaxLength ? "#d32f2f" : PureLightThemeCaaS.colors.alpha.white[30],
                },
              },
              "& .MuiInputBase-input::placeholder": {
                color: PureLightThemeCaaS.colors.alpha.white[50],
                opacity: 1,
              },
              "& .MuiInputBase-input.Mui-disabled::placeholder": {
                color: PureLightThemeCaaS.colors.alpha.white[50],
                opacity: 1,
                WebkitTextFillColor: PureLightThemeCaaS.colors.alpha.white[50],
              },
            }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            width: "90%",
            maxWidth: "90%",
            margin: "0 auto",
            pb: 0.5,
          }}
        >
          <Typography sx={{ color: PureLightThemeCaaS.colors.alpha.white[70], fontSize: "10px", fontWeight: "bold" }}>
            {textInput.length}/{maxLength}
          </Typography>
        </Box>
      </Box>
    );
  },
);

export default ChatBotQuestionInput;
